import {
  LinkIcon,
  MessageCircle,
  CheckCircle,
  Clock,
  AlertTriangle,
  Video,
  Loader2,
  Play,
  Image,
  Layers,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Platform, PostStatus, MediaType } from "@/types";
import type { FeedFilters, Platform as PlatformType, MediaType as MediaTypeType } from "@/types";

// ============================================
// PLATFORM CONFIGURATION
// ============================================

export interface PlatformConfig {
  id: PlatformType;
  label: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
}

export const PLATFORMS: PlatformConfig[] = [
  {
    id: Platform.X,
    label: "X / Twitter",
    icon: MessageCircle,
    color: "text-sky-500",
    bgColor: "bg-sky-500/10",
  },
  {
    id: Platform.Instagram,
    label: "Instagram",
    icon: Image,
    color: "text-pink-500",
    bgColor: "bg-pink-500/10",
  },
  {
    id: Platform.TikTok,
    label: "TikTok",
    icon: Video,
    color: "text-cyan-500",
    bgColor: "bg-cyan-500/10",
  },
  {
    id: Platform.Facebook,
    label: "Facebook",
    icon: MessageCircle,
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
  },
  {
    id: Platform.YouTube,
    label: "YouTube",
    icon: Play,
    color: "text-red-500",
    bgColor: "bg-red-500/10",
  },
  {
    id: Platform.LinkedIn,
    label: "LinkedIn",
    icon: MessageCircle,
    color: "text-blue-700",
    bgColor: "bg-blue-700/10",
  },
  {
    id: Platform.Pinterest,
    label: "Pinterest",
    icon: Image,
    color: "text-red-600",
    bgColor: "bg-red-600/10",
  },
  {
    id: Platform.Bluesky,
    label: "Bluesky",
    icon: MessageCircle,
    color: "text-blue-400",
    bgColor: "bg-blue-400/10",
  },
  {
    id: Platform.Threads,
    label: "Threads",
    icon: MessageCircle,
    color: "text-slate-700",
    bgColor: "bg-slate-700/10",
  },
] as const;

export const PLATFORM_MAP: Record<PlatformType, PlatformConfig> = Object.fromEntries(
  PLATFORMS.map((p) => [p.id, p]),
) as Record<PlatformType, PlatformConfig>;

// ============================================
// STATUS BADGES
// ============================================

export interface StatusBadgeConfig {
  label: string;
  icon: LucideIcon;
  color: string;
  bg: string;
}

export const STATUS_BADGE_CONFIG: Record<PostStatus | "default", StatusBadgeConfig> = {
  [PostStatus.Draft]: {
    label: "Draft",
    icon: LinkIcon,
    color: "text-slate-600",
    bg: "bg-slate-50",
  },
  [PostStatus.Scheduled]: {
    label: "Scheduled",
    icon: Clock,
    color: "text-amber-600",
    bg: "bg-amber-50",
  },
  [PostStatus.Processing]: {
    label: "Processing",
    icon: Loader2,
    color: "text-blue-600",
    bg: "bg-blue-50",
  },
  [PostStatus.Processed]: {
    label: "Processed",
    icon: CheckCircle,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
  },
  default: {
    label: "Unknown",
    icon: AlertTriangle,
    color: "text-slate-500",
    bg: "bg-slate-50",
  },
};

// ============================================
// FILTERS
// ============================================

export interface FilterOption<T = string> {
  id: T;
  label: string;
  icon?: LucideIcon;
}

// Status filters — ONLY real statuses, no 'all'
export const POST_STATUS_FILTERS: FilterOption<PostStatus>[] = [
  { id: PostStatus.Processed, label: "Processed", icon: CheckCircle },
  { id: PostStatus.Scheduled, label: "Scheduled", icon: Clock },
  { id: PostStatus.Draft, label: "Draft", icon: LinkIcon },
  { id: PostStatus.Processing, label: "Processing", icon: Loader2 },
];

// Platform filters — real platforms only
export const PLATFORM_FILTERS: FilterOption<PlatformType>[] = PLATFORMS.map((p) => ({
  id: p.id,
  label: p.label,
  icon: p.icon,
}));

// Media type filters — real types only
export const MEDIA_TYPE_FILTERS: FilterOption<MediaTypeType>[] = [
  { id: MediaType.Image, label: "Images", icon: Image },
  { id: MediaType.Video, label: "Videos", icon: Video },
  { id: MediaType.Carousel, label: "Carousels", icon: Layers },
];

// ============================================
// DEFAULT FILTER STATE
// ============================================
// No sentinel 'all' values — optional means "no filter"

export const DEFAULT_FEED_FILTERS: FeedFilters = {
  status: PostStatus.Processed,
  limit: 20,
  offset: 0,
};

// ============================================
// QUERY KEYS
// ============================================

export const QUERY_KEYS = {
  socialPosts: (filters: FeedFilters) =>
    ["social-posts", filters.status, filters.platform, filters.mediaType, filters.offset] as const,
  accountFeed: (accountId: string, filters: { platform?: PlatformType; cursor?: string }) =>
    ["account-feed", accountId, filters.platform, filters.cursor] as const,
} as const;
